import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalFonts,
  signalRadii,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  GlassCard,
  Mono,
  ScreenFrame,
  SectionLabel,
  SignalButton,
  StatusPill,
} from '@/common/components/signal';

const SETTINGS_NAV: [string, boolean][] = [
  ['Profile', false],
  ['Plan & storage', true],
  ['Devices', false],
  ['Notifications', false],
  ['Security', false],
];

/**
 * What the vault is actually full of. Percentages are of the whole plan, not of
 * the used portion, so they sum to the 71% the meter reports rather than to 100.
 */
const STORAGE: { label: string; pct: number; color: string }[] = [
  { label: 'SESSIONS 8.2 GB', pct: 41, color: signalColors.accent },
  { label: 'STEMS 3.8 GB', pct: 19, color: signalColors.cyan },
  { label: 'OLD VERSIONS 2.2 GB', pct: 11, color: signalColors.green },
];

const STATS: [string, string][] = [
  ['PROJECTS', '18'],
  ['VERSIONS STORED', '211'],
  ['TRANSFERRED · AUG', '64 GB'],
];

const INVOICES: [string, string, string][] = [
  ['AUG 3, 2026', 'Pro — monthly', '$14.00'],
  ['JUL 3, 2026', 'Pro — monthly', '$14.00'],
  ['JUN 3, 2026', 'Pro — monthly', '$14.00'],
];

/** Display-font number over a mono rubric — the account's three headline counts. */
const StatCard: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      border: `1px solid ${signalBorders.default}`,
      borderRadius: `${signalRadii.xl}px`,
      background: signalSurfaces.fillSubtle,
      padding: '16px',
    }}
  >
    <Mono size={10} weight={600}>
      {label}
    </Mono>
    <Box
      sx={{
        fontFamily: signalFonts.display,
        fontWeight: 700,
        fontSize: '24px',
        marginTop: '6px',
      }}
    >
      {value}
    </Box>
  </Box>
);

/**
 * 12 — Account & billing.
 *
 * The storage meter is the argument the whole screen is built around: a single
 * "71% full" bar tells you to buy more space, while the same bar split into
 * sessions / stems / old versions tells you that 2.2 GB of it is versions you
 * could trim. Both readings are true; only the second one is actionable, which
 * is why the upgrade dialog beside it offers trimming as a real alternative
 * rather than as a token second button.
 */
export const Screen12Billing: React.FC = () => (
  <ScreenFrame
    index="12"
    title="Account & billing"
    note="SETTINGS · UPGRADE DIALOG"
    bare
  >
    <Box
      sx={{
        display: 'flex',
        gap: '24px',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          width: 940,
          maxWidth: '100%',
          borderRadius: `${signalRadii['3xl']}px`,
          border: `1px solid ${signalBorders.medium}`,
          background: signalSurfaces.screen,
          boxShadow: '0 40px 90px -40px rgba(0,0,0,0.95)',
          padding: '26px 28px 30px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '24px', marginBottom: '26px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
              width: 170,
              flex: 'none',
            }}
          >
            {SETTINGS_NAV.map(([label, active]) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 12px',
                  borderRadius: `${signalRadii.md}px`,
                  fontSize: '13px',
                  ...(active
                    ? {
                        background: 'rgba(255,106,31,0.13)',
                        color: signalText.primary,
                        fontWeight: 600,
                      }
                    : {
                        color: signalText.secondary,
                        cursor: 'pointer',
                        '&:hover': {
                          background: signalSurfaces.fillStrong,
                          color: signalText.primary,
                        },
                      }),
                }}
              >
                {active && (
                  <Box
                    component="span"
                    sx={{
                      width: 5,
                      height: 16,
                      borderRadius: '3px',
                      background: signalColors.accent,
                    }}
                  />
                )}
                {label}
              </Box>
            ))}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              component="h3"
              sx={{
                margin: '0 0 18px',
                fontFamily: signalFonts.display,
                fontWeight: 800,
                fontSize: '30px',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}
            >
              Plan &amp; storage
            </Box>

            <Box
              sx={{
                border: '1px solid rgba(255,106,31,0.35)',
                background: 'rgba(255,106,31,0.07)',
                borderRadius: `${signalRadii['2xl']}px`,
                padding: '20px',
                marginBottom: '16px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                  marginBottom: '16px',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: signalFonts.display,
                        fontWeight: 700,
                        fontSize: '20px',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      Pro
                    </Box>
                    <StatusPill
                      borderless
                      tone={{
                        fg: signalColors.accentBright,
                        bg: 'rgba(255,106,31,0.18)',
                        border: 'transparent',
                      }}
                      sx={{ padding: '3px 8px' }}
                    >
                      ACTIVE
                    </StatusPill>
                  </Box>
                  <Mono
                    size={11}
                    color={signalText.secondary}
                    sx={{ display: 'block', marginTop: '7px' }}
                  >
                    $14 / MONTH · RENEWS SEP 3, 2026 · VISA ···· 4417
                  </Mono>
                </Box>
                <SignalButton
                  variant="secondary"
                  sx={{
                    padding: '9px 14px',
                    fontSize: '12.5px',
                    borderRadius: '9px',
                    background: signalSurfaces.fillActive,
                    borderColor: signalBorders.control,
                    '&:hover': { background: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  Manage billing
                </SignalButton>
                <SignalButton
                  glow={false}
                  sx={{
                    padding: '9px 15px',
                    fontSize: '12.5px',
                    borderRadius: '9px',
                  }}
                >
                  Upgrade to Studio
                </SignalButton>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '9px',
                }}
              >
                <Mono size={11} color={signalText.secondary}>
                  VAULT USED
                </Mono>
                <Mono size={11} color={signalColors.amber}>
                  14.2 / 20 GB · 71%
                </Mono>
              </Box>

              {/* One track, three fills laid end to end — the segments are
                  flex children rather than absolutely positioned, so the bar
                  can't drift out of sync with the legend beneath it. */}
              <Box
                role="img"
                aria-label="Vault storage by category: 71% of 20 GB used"
                sx={{
                  height: 8,
                  borderRadius: '999px',
                  background: signalSurfaces.track,
                  overflow: 'hidden',
                  display: 'flex',
                }}
              >
                {STORAGE.map((segment) => (
                  <Box
                    key={segment.label}
                    sx={{
                      width: `${segment.pct}%`,
                      height: '100%',
                      background: segment.color,
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: '18px', marginTop: '12px' }}>
                {STORAGE.map((segment) => (
                  <Box
                    key={segment.label}
                    sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '2px',
                        background: segment.color,
                      }}
                    />
                    <Mono size={10.5}>{segment.label}</Mono>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '20px',
              }}
            >
              {STATS.map(([label, value]) => (
                <StatCard key={label} label={label} value={value} />
              ))}
            </Box>

            <SectionLabel
              size={10}
              sx={{ display: 'block', marginBottom: '12px' }}
            >
              INVOICES
            </SectionLabel>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              {INVOICES.map(([date, description, amount]) => (
                <Box
                  key={date}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '11px 12px',
                    borderRadius: '9px',
                    fontSize: '13px',
                    color: signalText.secondary,
                    cursor: 'pointer',
                    '&:hover': { background: 'rgba(255,255,255,0.045)' },
                  }}
                >
                  <Mono
                    size={11}
                    color={signalText.primary}
                    sx={{ width: 110, flex: 'none' }}
                  >
                    {date}
                  </Mono>
                  <Box sx={{ flex: 1 }}>{description}</Box>
                  <Mono size={11}>{amount}</Mono>
                  <Mono
                    size={9.5}
                    weight={600}
                    color={signalColors.green}
                    sx={{ width: 58, flex: 'none', textAlign: 'right' }}
                  >
                    PAID
                  </Mono>
                  <Box component="span" sx={{ color: signalText.muted }}>
                    ↓
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Upgrade dialog — triggered by a save that won't fit, so it leads with
          the blocked action rather than with the plan it wants to sell. */}
      <GlassCard
        width={440}
        padding="26px"
        sx={{ background: signalSurfaces.glassStrong }}
      >
        <StatusPill
          status="behind"
          dot
          sx={{ marginBottom: '16px', padding: '5px 10px' }}
        >
          VAULT 94% FULL
        </StatusPill>

        <Box
          component="h3"
          sx={{
            margin: '0 0 8px',
            fontFamily: signalFonts.display,
            fontWeight: 800,
            fontSize: '26px',
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
          }}
        >
          Nightdrive v15 needs 2.1 GB
        </Box>
        <Box
          component="p"
          sx={{
            margin: '0 0 20px',
            fontSize: '13px',
            lineHeight: 1.6,
            color: signalText.secondary,
          }}
        >
          You have 1.2 GB left on Pro. Upgrade to Studio for 500 GB, or free up
          space by trimming old versions.
        </Box>

        {/* Ramps accent → amber across its own length, so the bar reads as
            approaching a limit rather than simply being long. */}
        <Box
          sx={{
            height: 8,
            borderRadius: '999px',
            background: signalSurfaces.track,
            overflow: 'hidden',
            marginBottom: '8px',
          }}
        >
          <Box
            sx={{
              width: '94%',
              height: '100%',
              borderRadius: '999px',
              background: signalColors.accent,
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <Mono size={10.5}>18.8 GB USED</Mono>
          <Mono size={10.5}>20 GB</Mono>
        </Box>

        <Box
          sx={{
            border: `1px solid ${signalStatus.stems.border}`,
            background: 'rgba(85,200,232,0.07)',
            borderRadius: `${signalRadii.xl}px`,
            padding: '16px',
            marginBottom: '18px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <Box
              component="span"
              sx={{
                fontFamily: signalFonts.display,
                fontWeight: 700,
                fontSize: '18px',
              }}
            >
              Studio
            </Box>
            <Mono size={11} color={signalColors.cyan}>
              $39 / MO · 500 GB
            </Mono>
          </Box>
          <Box
            sx={{
              fontSize: '12.5px',
              color: signalText.secondary,
              marginTop: '8px',
              lineHeight: 1.5,
            }}
          >
            Prorated today:{' '}
            <Box component="b" sx={{ color: signalText.primary }}>
              $21.40
            </Box>{' '}
            for the rest of this cycle.
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '10px' }}>
          <SignalButton
            variant="secondary"
            sx={{
              flex: 1,
              padding: '11px',
              borderRadius: `${signalRadii.md}px`,
              background: 'transparent',
              color: signalText.secondary,
              borderColor: signalBorders.stronger,
            }}
          >
            Trim old versions
          </SignalButton>
          <SignalButton
            glow={false}
            sx={{
              flex: 1,
              padding: '11px',
              borderRadius: `${signalRadii.md}px`,
            }}
          >
            Upgrade
          </SignalButton>
        </Box>
      </GlassCard>
    </Box>
  </ScreenFrame>
);

export default Screen12Billing;
